import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import CountryActionLink from "./CountryActionLink";
import CountryMapGraphic from "./CountryMapGraphic";
import CountryFlagIcon from "../common/CountryFlagIcon";

const reveal = {
  hidden: { opacity: 0, y: 22 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay,
      duration: 0.65,
      ease: [0.16, 1, 0.3, 1]
    }
  })
};

function CountryHero({ country }) {
  return (
    <section className="relative overflow-hidden pb-16 pt-28 md:pb-24 md:pt-36">
      <div className={`absolute inset-0 bg-gradient-to-br ${country.mood.gradient}`} />
      <div className="absolute inset-0 bg-white/40 backdrop-blur-[2px]" />
      <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 19px, rgba(7,18,38,0.03) 19px, rgba(7,18,38,0.03) 20px)' }} />

      <div className="relative mx-auto grid w-full max-w-7xl gap-12 px-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
        <div>
          <motion.div
            custom={0.04}
            variants={reveal}
            initial="hidden"
            animate="visible"
            className="mb-6 inline-flex items-center gap-2 rounded-sm bg-midnight-navy px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-soft-white shadow-md"
          >
            <Link to="/" className="hover:text-warm-gold transition-colors">
              Home
            </Link>
            <ChevronRight className="h-3.5 w-3.5 opacity-50" aria-hidden="true" />
            <span className="text-warm-gold">Study in {country.name}</span>
          </motion.div>

          <motion.div
            custom={0.16}
            variants={reveal}
            initial="hidden"
            animate="visible"
            className="border-l-4 border-midnight-navy pl-6 mb-6"
          >
            <div className="flex items-end gap-4 mb-2">
              <CountryFlagIcon
                slug={country.slug}
                name={country.name}
                width={48}
                height={34}
                className="drop-shadow-md pb-1"
              />
              <h1 className="text-3xl leading-[1.05] font-black tracking-tight text-midnight-navy sm:text-4xl md:text-6xl">
                {country.hero.headline}
              </h1>
            </div>
            
            <p className="mt-5 max-w-2xl text-sm font-medium leading-relaxed text-slate-text/90 sm:text-base md:text-lg">
              {country.hero.subheadline}
            </p>
          </motion.div>

          <motion.div
            custom={0.28}
            variants={reveal}
            initial="hidden"
            animate="visible"
            className="mt-6 flex flex-wrap gap-2 pl-7"
          >
            {country.hero.supportCards.map((card) => (
              <span
                key={card}
                className="rounded-sm border-l-2 border-warm-gold bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-midnight-navy shadow-sm"
              >
                {card}
              </span>
            ))}
          </motion.div>

          <motion.div
            custom={0.34}
            variants={reveal}
            initial="hidden"
            animate="visible"
            className="mt-8 flex flex-col gap-3 sm:flex-row pl-7"
          >
            <CountryActionLink variant="primary" to={country.hero.ctaPrimary.to} label={country.hero.ctaPrimary.label} />
            <CountryActionLink variant="secondary" to={country.hero.ctaSecondary.to} label={country.hero.ctaSecondary.label} />
          </motion.div>

          <motion.div
            custom={0.38}
            variants={reveal}
            initial="hidden"
            animate="visible"
            className="mt-10 grid grid-cols-1 sm:grid-cols-3 bg-white border border-midnight-navy/10 shadow-soft rounded-lg overflow-hidden ml-7"
          >
            {country.hero.quickFacts.map((fact, idx) => (
              <div key={fact.label} className={`px-5 py-4 ${idx > 0 && "sm:border-l border-midnight-navy/10"}`}>
                <p className={`text-xl font-black tracking-tight sm:text-2xl ${country.mood.accentText}`}>{fact.value}</p>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-text/70">{fact.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative space-y-5">
          {country.heroImage ? (
            <div className="overflow-hidden rounded-[1.6rem] border border-white/40 bg-white/80 shadow-luxury">
              <img
                src={country.heroImage}
                alt={country.heroImageAlt || `${country.name} destination`}
                className="h-40 w-full object-cover sm:h-48"
                loading="lazy"
              />
            </div>
          ) : null}
          <div className="relative">
            <div className="absolute inset-0 bg-white/20 blur-3xl rounded-full" />
            <CountryMapGraphic mapKey={country.mapKey} mapGlow={country.mood.mapGlow} />
          </div>
        </div>
      </div>
    </section>
  );
}

export default CountryHero;
